import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { finMargenPagoFijo } from '#components/mobile/budget/pagosFijos';
import type { PagoFijo } from '#components/mobile/budget/pagosFijos';
import { usePagosFijos } from '#components/mobile/budget/usePagosFijos';

export type PagoVencido = {
  /** Id de la categoría (pago fijo). */
  id: string;
  nombre: string;
  /** Importe del objetivo (céntimos, positivo). */
  importe: IntegerAmount;
  /** Fecha del cargo `YYYY-MM-DD`. */
  fecha: string;
  /** Cuenta de cargo (guardada o la más frecuente del historial). */
  cuentaId: string | null;
  /** Mes del cargo `YYYY-MM` (el de la marca «Ya pagado» y la ficha). */
  mes: string;
};

/**
 * Pagos fijos atrasados: pasó el día de cargo y el margen de
 * `MARGEN_DIAS_PAGO_FIJO` días y aún no cuentan como pagados. Sin fecha o
 * sin importe no salen (nunca un falso «Atrasado»). `anteriores` son los
 * del mes anterior: entran solo los que pasan a atrasados este mes (cargo
 * a final de mes cuyo margen cruza al mes siguiente, p. ej. día 30 + 3).
 */
export function pagosVencidos(
  pagos: readonly PagoFijo[],
  anteriores: readonly PagoFijo[] = [],
  mesActual?: string,
): PagoVencido[] {
  const atrasado = (p: PagoFijo) =>
    p.estado === 'atrasado' && !!p.fecha && !!(p.importeCargo ?? p.importe);
  const delMesAnterior = (p: PagoFijo) =>
    atrasado(p) &&
    mesActual != null &&
    p.fecha!.slice(0, 7) < mesActual &&
    finMargenPagoFijo(p.fecha!).slice(0, 7) === mesActual;
  const vistos = new Set<string>();
  return [...anteriores.filter(delMesAnterior), ...pagos.filter(atrasado)]
    .filter(p => {
      const clave = `${p.id}/${p.fecha}`;
      if (vistos.has(clave)) return false;
      vistos.add(clave);
      return true;
    })
    .map(p => ({
      id: p.id,
      nombre: p.nombre,
      importe: p.importeCargo ?? p.importe ?? 0,
      fecha: p.fecha ?? '',
      cuentaId: p.cuentaId,
      mes: (p.fecha ?? '').slice(0, 7),
    }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

export type CambioCuentaPendiente = {
  /** Id de la categoría (pago fijo). */
  id: string;
  nombre: string;
  /** Movimiento que salió de la otra cuenta. */
  movimientoId: string;
  /** Cuenta nueva (la del último cargo). */
  cuentaId: string;
};

/**
 * Pagos fijos cuyo último cargo salió de otra cuenta: «¿A partir de ahora
 * sale de ahí?». Por nombre.
 */
export function cambiosDeCuenta(
  pagos: readonly PagoFijo[],
): CambioCuentaPendiente[] {
  const r: CambioCuentaPendiente[] = [];
  for (const p of pagos) {
    if (p.cambioCuenta) {
      r.push({
        id: p.id,
        nombre: p.nombre,
        movimientoId: p.cambioCuenta.movimientoId,
        cuentaId: p.cambioCuenta.cuentaId,
      });
    }
  }
  return r.sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/**
 * Pagos fijos atrasados (de este mes y los de final del anterior cuyo
 * margen cruza a este), para «Por hacer» de Inicio, la marca manual «Ya
 * pagado» y los cambios de cuenta de cargo por responder.
 */
export function usePagosVencidos() {
  const mesActual = monthUtils.currentMonth();
  const mesAnterior = monthUtils.subMonths(mesActual, 1);
  const { pagos, cargando, marcar, setCuenta, ignorarCambio } =
    usePagosFijos(mesActual);
  const anterior = usePagosFijos(mesAnterior);
  const vencidos = useMemo(
    () => pagosVencidos(pagos, anterior.pagos, mesActual),
    [pagos, anterior.pagos, mesActual],
  );
  const cambios = useMemo(() => cambiosDeCuenta(pagos), [pagos]);
  return {
    vencidos,
    cambios,
    cargando: cargando || anterior.cargando,
    /** Marca «Ya pagado» en el mes del cargo (este o el anterior). */
    marcarPagado: (id: string, mes: string = mesActual) =>
      mes === mesAnterior ? anterior.marcar(id, true) : marcar(id, true),
    aceptarCambio: (c: CambioCuentaPendiente) => setCuenta(c.id, c.cuentaId),
    ignorarCambio: (c: CambioCuentaPendiente) => ignorarCambio(c.movimientoId),
  };
}
